// ModelManager.js
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { LAT, LNG, MODEL_URL } from "../../helpers/CONSTANT";
import { VARS, markModelLoaded, resetModelLoaded } from "../Global/variables";
import toast from "../../lib/toast";

export class ModelManager {
  constructor(scene, setModelProgress, currentDesign) {
    this.scene = scene;
    this.setModelProgress = setModelProgress;
    this.currentDesign = currentDesign;
    this.modelMeshs = [];

    this.boxMinz = 0;

   
    resetModelLoaded();

    // Three.js loading manager for progress + error tracking
    this.loadingManager = new THREE.LoadingManager();
    this.loadingManager.onProgress = this.onProgress.bind(this);
    this.loadingManager.onError = this.onError.bind(this);
   
    this.loadingManager.onLoad = () => markModelLoaded();

    this.isDevMode = (process.env.REACT_APP_DEV_MODE || "").toLowerCase() === "true";

    // Local model path (served from /public)
    // Put your file here: public/local_model/model.glb
    this.localModelPath = "/local_model/model.glb";

    this.loadModel();
    this.addFloor();
    this.addLights();

    return this.modelMeshs;
  }

  loadModel() {
    const loader = new GLTFLoader(this.loadingManager);

    // Choose which model URL to load
    const modelUrl = this.isDevMode ? this.localModelPath : this.currentDesign?.model;

    if (!modelUrl) {
      console.error(" Model URL is missing. currentDesign?.model is empty and dev mode is off.");
      
      markModelLoaded();
      return;
    }

    loader.load(
      modelUrl,
      (gltf) => {
        const root = gltf.scene;

        // Normalize Z so model sits on floor
        this.setModel(root);

        // Store all nodes/meshes
        root.traverse((child) => {
          this.modelMeshs.push(child);
          VARS.Meshes.push(child);
        });

        root.name = "houseModel";
        this.scene.add(root);
      },
      undefined,
      (err) => {
        console.error(`❌ Failed to load model: ${modelUrl}`, err);
      }
    );
  }

  // Normalize model vertical position so the base touches the floor
  setModel(root) {
    const bbox = new THREE.Box3().setFromObject(root);
    this.boxMinz = bbox.min.z;
    root.position.z -= this.boxMinz;
  }

  addFloor() {
    const geometry = new THREE.BoxGeometry(130, 130, 0);

    const address = this.resolveFloorTileUrl();

    const texture = new THREE.TextureLoader().load(
      address,
      undefined,
      undefined,
      () => {
        // The satellite base map failed to load (missing/invalid key, quota,
        // tile 404, or CORS). The scene still works with a blank floor.
        toast.warning("Couldn't load the satellite map image.", {
          key: "map-tile-failed",
          description: "Design still works, but the aerial background is unavailable.",
          duration: 6000,
        });
      }
    );
    const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.8 });

    const cube = new THREE.Mesh(geometry, material);
    cube.userData.name = "ground";
    cube.userData.mapTileProvider = "google-static";
    cube.position.set(0, 0, 0);
    this.scene.add(cube);

    VARS.mapTile = cube;

    const renderTarget = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight);
    renderTarget.texture.colorSpace = THREE.SRGBColorSpace;
  }

  resolveFloorTileUrl() {
    const latitude = Number.parseFloat(this.currentDesign?.latitude ?? LAT);
    const longitude = Number.parseFloat(this.currentDesign?.longitude ?? LNG);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      try {
        const defaultMapUrl = new URL(MODEL_URL);
        const googleMapsApiKey = defaultMapUrl.searchParams.get("key");

        if (googleMapsApiKey) {
          return `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&size=1200x1200&scale=2&zoom=19&maptype=satellite&key=${googleMapsApiKey}`;
        }
      } catch (error) {
        console.warn("Failed to build Google Maps tile URL.", error);
      }
    }

    return MODEL_URL;
  }

  addLights() {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.75);
    directionalLight.position.set(0, 0, -2);
    this.scene.add(directionalLight);
  }

  // LoadingManager progress signature: (url, itemsLoaded, itemsTotal)
  onProgress(url, loaded, total) {
    const progress = (loaded / total) * 100;
    this.setModelProgress(progress.toFixed(2));
  }

  onError(url) {
    console.error(`Error loading ${url}`);
  }
}
